import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { apiService } from '../services/apiService';

const SuperAdminGrievancePage: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const query = useMemo(() => ({ search: search.trim() || undefined }), [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAdminTenantGrievances(query);
      setRows(data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load grievances');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <ReportProblemIcon />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Grievance Management
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search tenant"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button variant="outlined" onClick={load} disabled={loading}>
            Reload
          </Button>
        </Box>
      </Paper>

      {loading && <CircularProgress size={22} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tenant ID</TableCell>
              <TableCell>Tenant Name</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Tenant Status</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Active Members</TableCell>
              <TableCell>Licenses</TableCell>
              <TableCell>Grievance Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Last Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, index) => (
              <TableRow key={r.tenantId}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{r.tenantName || '-'}</TableCell>
                <TableCell>{r.tenantSlug || '-'}</TableCell>
                <TableCell>{r.tenantStatus || '-'}</TableCell>
                <TableCell>{r.planType || '-'}</TableCell>
                <TableCell>{r.activeMemberCount ?? 0}</TableCell>
                <TableCell>{r.licenseCount ?? '-'}</TableCell>
                <TableCell>{r.grievanceStatus || '-'}</TableCell>
                <TableCell>{r.grievancePriority || '-'}</TableCell>
                <TableCell>{r.grievanceSummary || '-'}</TableCell>
                <TableCell>{r.lastUpdatedAt || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default SuperAdminGrievancePage;

